/**
 * Система логирования для отладки
 * Пишет логи в консоль и в файл через API
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  data?: any;
  page: string;
  userAgent: string;
}

class Logger {
  private static instance: Logger;
  private logs: LogEntry[] = [];
  private enabled: boolean = true;
  private sendToServer: boolean = true;

  private constructor() {
    this.enabled = import.meta.env.DEV;
    this.sendToServer = import.meta.env.DEV;
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private createLog(level: LogLevel, message: string, data?: any): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
      page: window.location.href,
      userAgent: navigator.userAgent,
    };

    this.logs.push(entry);

    // Сохраняем последние 1000 логов в памяти
    if (this.logs.length > 1000) {
      this.logs.shift();
    }

    return entry;
  }

  private outputToConsole(entry: LogEntry) {
    const prefix = `[${entry.timestamp.split('T')[1].split('.')[0]}]`;
    const emoji = {
      debug: '🔍',
      info: 'ℹ️',
      warn: '⚠️',
      error: '❌',
    }[entry.level];

    const consoleMethod = entry.level === 'error' ? 'error' : 
                         entry.level === 'warn' ? 'warn' : 'log';

    console[consoleMethod](`${prefix} ${emoji} [${entry.level.toUpperCase()}] ${entry.message}`, entry.data || '');
  }

  private async sendToBackend(entry: LogEntry) {
    if (!this.sendToServer) return;

    try {
      await fetch('/api/client-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      });
    } catch (e) {
      // Игнорируем ошибки логирования
    }
  }

  private log(level: LogLevel, message: string, data?: any) {
    if (!this.enabled) return;

    const entry = this.createLog(level, message, data);
    this.outputToConsole(entry);
    
    // Отправляем на сервер только warn и error
    if (level === 'warn' || level === 'error') {
      this.sendToBackend(entry);
    }
  }

  debug(message: string, data?: any) {
    this.log('debug', message, data);
  }

  info(message: string, data?: any) {
    this.log('info', message, data);
  }

  warn(message: string, data?: any) {
    this.log('warn', message, data);
  }

  error(message: string, data?: any) {
    this.log('error', message, data);
  }

  // Получение всех логов
  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  // Очистка логов
  clear() {
    this.logs = [];
  }

  // Экспорт логов в файл
  exportToFile() {
    const blob = new Blob([JSON.stringify(this.logs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `myevents-logs-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }
}

// Глобальный экземпляр
export const logger = Logger.getInstance();

// Перехват глобальных ошибок
if (import.meta.env.DEV) {
  window.addEventListener('error', (event) => {
    logger.error('Global error', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled promise rejection', {
      reason: event.reason?.message || event.reason,
    });
  });

  // Лог при загрузке страницы
  logger.info('Page loaded', {
    url: window.location.href,
    referrer: document.referrer,
    userAgent: navigator.userAgent,
    screen: `${window.screen.width}x${window.screen.height}`,
    language: navigator.language,
  });
}
